import { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, CircularProgress, TextField } from "@mui/material";

import { useUserSearch } from "../../../../../api/v1/queries/users.query";
import UserCard from "../../../../User/components/UserCard";

interface UsersTabProps {
  selectedUserIds: number[];
  onUserSelect: (userId: number) => void;
}

const UsersTab = ({ selectedUserIds, onUserSelect }: UsersTabProps) => {
  const { t } = useTranslation("feed", { keyPrefix: "postForm" });
  const listRef = useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = useState("");

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage } = useUserSearch({
    searchValue,
    limit: 15,
    offset: 0,
    enableInitialSearch: true,
  });

  useEffect(() => {
    const handleScroll = () => {
      if (!listRef.current || !hasNextPage || isFetchingNextPage) return;

      const { scrollTop, scrollHeight, clientHeight } = listRef.current;
      if (scrollHeight - scrollTop <= clientHeight * 1.5) {
        fetchNextPage();
      }
    };

    const currentListRef = listRef.current;
    if (currentListRef) {
      currentListRef.addEventListener("scroll", handleScroll);
      return () => currentListRef.removeEventListener("scroll", handleScroll);
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <>
      <TextField
        fullWidth
        placeholder={t("searchUsers")}
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        sx={{
          "& .MuiInputBase-root.MuiOutlinedInput-root": {
            borderRadius: "8px",
          },
          mb: 2,
        }}
      />

      <Box
        ref={listRef}
        sx={{
          height: "calc(100% - 123px)",
          overflowY: "auto",
          position: "relative",
          paddingRight: "10px",
          display: "flex",
          flexDirection: "column",
          rowGap: "16px",
        }}
      >
        {data?.pages.map((page) =>
          page.items.map((user) => (
            <UserCard
              key={user.id}
              user={user}
              variant="list"
              showCheckbox
              isSelected={selectedUserIds.includes(user.id)}
              onSelect={() => onUserSelect(user.id)}
              hasActions={false}
            />
          ))
        )}

        {(isLoading || isFetchingNextPage) && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>
    </>
  );
};

export default UsersTab;
